import { FC, useCallback, useState } from "react"
import { PollyCustomField } from "../../../types/pricing-engine"
import { PrimaryBtn, LinkBtnDanger } from "../buttons"
import { Plus, Trash2 } from "lucide-react"

interface Props {
  currentCustomFields: PollyCustomField[]
  onChange: (newCustomFields: PollyCustomField[]) => void
}

const PollyCustomFieldsManager: FC<Props> = ({
  currentCustomFields,
  onChange
}) => {
  const [newFieldName, setNewFieldName] = useState("")
  const [newFieldValue, setNewFieldValue] = useState("")

  const handleAddField = useCallback(() => {
    const trimmedName = newFieldName.trim()
    if (!trimmedName) return

    // Check for duplicate field names
    const isDuplicate = currentCustomFields.some(
      f => f.name.toLowerCase() === trimmedName.toLowerCase()
    )
    if (isDuplicate) {
      return // Don't add duplicate - button will be disabled anyway
    }

    const newField: PollyCustomField = {
      name: trimmedName,
      value: newFieldValue.trim()
    }

    onChange([...currentCustomFields, newField])
    setNewFieldName("")
    setNewFieldValue("")
  }, [newFieldName, newFieldValue, currentCustomFields, onChange])

  // Check if the current name would be a duplicate
  const isDuplicateName = currentCustomFields.some(
    f => f.name.toLowerCase() === newFieldName.trim().toLowerCase()
  )

  const handleUpdateField = useCallback(
    (index: number, field: "name" | "value", value: string) => {
      const updated = currentCustomFields.map((cf, i) =>
        i === index ? { ...cf, [field]: value } : cf
      )
      onChange(updated)
    },
    [currentCustomFields, onChange]
  )

  const handleDeleteField = useCallback(
    (index: number) => {
      const updated = currentCustomFields.filter((_, i) => i !== index)
      onChange(updated)
    },
    [currentCustomFields, onChange]
  )

  const inputClassName =
    "block w-full px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-4">
          Configure custom fields for Polly pricing requests. These key-value
          pairs will be included with each pricing request.
        </p>

        {/* Existing Fields */}
        {currentCustomFields.length > 0 && (
          <div className="space-y-3 mb-4">
            {currentCustomFields.map((field, index) => (
              <div key={index} className="flex gap-3 items-center">
                <div className="flex-1">
                  <input
                    type="text"
                    value={field.name}
                    onChange={e =>
                      handleUpdateField(index, "name", e.target.value)
                    }
                    placeholder="Field name"
                    className={inputClassName}
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    value={field.value}
                    onChange={e =>
                      handleUpdateField(index, "value", e.target.value)
                    }
                    placeholder="Field value"
                    className={inputClassName}
                  />
                </div>
                <LinkBtnDanger
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDeleteField(index)
                  }}
                  className="p-2"
                >
                  <Trash2 size={16} />
                </LinkBtnDanger>
              </div>
            ))}
          </div>
        )}

        {/* Add New Field */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h5 className="text-sm font-medium mb-3">Add New Field</h5>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={newFieldName}
                onChange={e => setNewFieldName(e.target.value)}
                placeholder="e.g. customerId"
                className={inputClassName}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="text"
                value={newFieldValue}
                onChange={e => setNewFieldValue(e.target.value)}
                placeholder="e.g. 12345"
                className={inputClassName}
              />
            </div>
            <PrimaryBtn
              type="button"
              onClick={handleAddField}
              disabled={!newFieldName.trim() || isDuplicateName}
              className="flex items-center"
            >
              <Plus size={16} className="mr-2" />
              Add
            </PrimaryBtn>
          </div>
          {isDuplicateName && newFieldName.trim() && (
            <p className="text-sm text-red-600 mt-2">
              A field with this name already exists.
            </p>
          )}
        </div>
      </div>

      {currentCustomFields.length === 0 && (
        <div className="text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
          No custom fields configured. Add fields above.
        </div>
      )}
    </div>
  )
}

export default PollyCustomFieldsManager
